// src/pages/OrderTaxInvoiceReceiptPage.tsx

import React, { useRef } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import { useReactToPrint } from "react-to-print";

import { useOrderView } from "../hooks/useOrderView";
import TaxInvoice from "./TaxInvoice";
import { mapTemplate } from "./mapTemplate";
import ErrorBoundary from "./errorBoundary";

export default function OrderTaxInvoiceReceiptPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const parsedOrderId = Number(orderId);

  const { data, isLoading, error } = useOrderView(parsedOrderId);
  const invoiceRef = useRef<HTMLDivElement>(null);

  /**
   * Print invoice.
   *
   * react-to-print is already imported normally because printing
   * is part of this page's functionality.
   */
  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: data?.invoice
      ? `Invoice-${data.invoice.invoice_number}`
      : "Invoice",
  });

  /**
   * Download invoice as PDF.
   *
   * IMPORTANT:
   * html2pdf.js is intentionally NOT imported at module level.
   *
   * This keeps html2pdf + html2canvas + jsPDF out of the
   * initial application bundle.
   */
  const handleDownloadPdf = async () => {
    if (!invoiceRef.current) {
      return;
    }

    try {
      // Lazy-load PDF dependencies only when the user requests a PDF.
      const { default: html2pdf } = await import("html2pdf.js");

      const invoiceNumber =
        data?.invoice?.invoice_number ?? "invoice";

      const options = {
        margin: 0.5,

        filename: `Invoice-${invoiceNumber}.pdf`,

        image: {
          type: "jpeg" as const,
          quality: 0.98,
        },

        html2canvas: {
          scale: 2,
        },

        jsPDF: {
          unit: "mm" as const,
          format: "a4" as const,
          orientation: "portrait" as const,
        },
      };

      await html2pdf()
        .set(options)
        .from(invoiceRef.current)
        .save();
    } catch (error) {
      console.error("Failed to generate invoice PDF:", error);

      // Optional:
      // Add your toast/notification here if your application has one.
      //
      // Example:
      // toast.error("Unable to generate PDF. Please try again.");
    }
  };

  // ------------------------------------------------------------
  // Validation / loading states
  // ------------------------------------------------------------

  if (!Number.isFinite(parsedOrderId) || parsedOrderId <= 0) {
    return (
      <div className="p-6 text-sm text-red-600">
        Invalid order ID.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Loading invoice...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-red-600">
        Unable to load invoice.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Invoice not found.
      </div>
    );
  }

  // ------------------------------------------------------------
  // Invoice data
  // ------------------------------------------------------------

  const templateData = mapTemplate(
    data.template?.invoice_template_data || {}
  );

  const invoice = data.invoice;

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <ErrorBoundary>
      <div className="p-6 flex flex-col items-center">

        {/* Action buttons */}
        <div className="flex justify-end gap-2 w-full max-w-[800px] mb-4">
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-white rounded-md border px-3 py-2 text-sm font-medium transition hover:bg-gray-50"
          >
            <Printer size={16} />
            Print
          </button>

          <button
            type="button"
            onClick={handleDownloadPdf}
            className="inline-flex items-center gap-2 bg-white rounded-md border px-3 py-2 text-sm font-medium transition hover:bg-gray-50"
          >
            <Download size={16} />
            Download
          </button>
        </div>

        {/* Invoice */}
        <div
          ref={invoiceRef}
          className="w-full max-w-[800px]"
        >
          <TaxInvoice
            order={data}
            invoice={invoice}
            templateData={templateData}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}